/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { Columns } from 'src/components/OrgAssociateTable/types';
import { MyOrganisationTabs } from 'src/components/Pages/MyOrganisation/MyOrganisationPage';
import { Order, Settings as TableSettings } from 'src/components/Table/types';
import { getOrgAssociateCenters } from 'src/services/apiService';
import { OrgAssociateCenter } from 'src/types';
import useAuth from './useAuth';

interface OrgAssociateCentersState {
  associateCenters: Array<OrgAssociateCenter>;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  associateCenterTableSettings: TableSettings;
  isCenterParent: boolean;
}
export interface OrgAssociateCenterContextState
  extends OrgAssociateCentersState {
  updateAssociateCenterTableSettings: (TableSettings) => void;
  updateSearchQuery: (string) => void;
  updateOrgId: (string) => void;
}

const defaultOrgAssociateCentersContext = {
  associateCenters: [],
  isLoading: false,
  error: null,
  searchQuery: '',
  associateCenterTableSettings: {
    order: Order.Asc,
    orderBy: Columns.name,
    page: 0,
    rowsPerPage: 15,
    count: 0,
  },
  isCenterParent: false,
  updateAssociateCenterTableSettings: () => {
    throw new Error(
      '<OrgAssociateCentersProvider> missing in a component tree'
    );
  },
  updateSearchQuery: () => {
    throw new Error(
      '<OrgAssociateCentersProvider> missing in a component tree'
    );
  },
  updateOrgId: () => {
    throw new Error(
      '<OrgAssociateCentersProvider> missing in a component tree'
    );
  },
};

const OrgAssociateCentersContext =
  createContext<OrgAssociateCenterContextState>(
    defaultOrgAssociateCentersContext
  );

const useOrgAssociateCenters = (): OrgAssociateCenterContextState =>
  useContext(OrgAssociateCentersContext);

export const OrgAssociateCentersProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const location = useLocation();
  const routeMatch = useRouteMatch();
  const history = useHistory();

  const {
    user: { organisationId },
    getCurrentOrg,
  } = useAuth();

  const [associateCenters, setAssociateCenters] = useState(
    defaultOrgAssociateCentersContext.associateCenters
  );
  const [isLoading, setIsLoading] = useState(
    defaultOrgAssociateCentersContext.isLoading
  );
  const [isCenterParent, setIsCenterParent] = useState(false);
  const [error, setError] = useState(defaultOrgAssociateCentersContext.error);
  const [orgId, setOrgId] = useState(organisationId);
  const [searchQuery, setSearchQuery] = useState(() => {
    const sessionTableSettings = JSON.parse(
      sessionStorage.getItem('tableSettings')
    );
    if (sessionTableSettings?.tab === MyOrganisationTabs.Users) {
      return sessionTableSettings.searchQuery;
    }
    return defaultOrgAssociateCentersContext.searchQuery;
  });

  const [associateCenterTableSettings, setAssociateCenterTableSettings] =
    useState(() => {
      const sessionTableSettings = JSON.parse(
        sessionStorage.getItem('tableSettings')
      );
      if (sessionTableSettings?.tab === MyOrganisationTabs.Users) {
        return sessionTableSettings.tableSettings;
      }
      return defaultOrgAssociateCentersContext.associateCenterTableSettings;
    });

  const updateAssociateCenterTableSettings = ({
    order,
    orderBy,
    page,
    rowsPerPage,
    count,
  }: TableSettings) => {
    setAssociateCenterTableSettings((prevState) => ({
      order: order ?? prevState.order,
      orderBy: (orderBy as Columns) ?? prevState.orderBy,
      page: page ?? prevState.page,
      rowsPerPage: rowsPerPage ?? prevState.rowsPerPage,
      count: count ?? prevState.count,
    }));
  };

  const updateSearchQuery = (value) => {
    setSearchQuery((prevState) => {
      if (prevState !== value) {
        updateAssociateCenterTableSettings({ ...prevState, page: 0 });
      }
      return value;
    });
  };

  const updateOrgId = (orgId: any) => {
    setOrgId(orgId);
  };

  const fetchAssociateCenters = () => {
    setIsLoading(true);
    setError(null);
    setTimeout(async () => {
      try {
        const data = await getOrgAssociateCenters(orgId);
        setAssociateCenters(data);
        // Determine if the current organisation is a parent centre by checking if it has any associated centres or the current organisation's center type
        setIsCenterParent(
          getCurrentOrg()?.centerType === 'parent' || data.length > 0
        );
      } catch (e) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (location.pathname === routeMatch.url) {
      const prevOrgId = sessionStorage.getItem('orgId');

      if (prevOrgId !== organisationId || history.action === 'PUSH') {
        sessionStorage.removeItem('tableSettings');
        setAssociateCenterTableSettings(
          defaultOrgAssociateCentersContext.associateCenterTableSettings
        );
      }
      fetchAssociateCenters();
    }
    sessionStorage.setItem('orgId', organisationId);
    return () => {
      setSearchQuery(defaultOrgAssociateCentersContext.searchQuery);
      setAssociateCenters(defaultOrgAssociateCentersContext.associateCenters);
      setIsLoading(defaultOrgAssociateCentersContext.isLoading);
      setError(defaultOrgAssociateCentersContext.error);
    };
  }, [orgId, location.pathname, routeMatch.url]);

  useEffect(() => {
    sessionStorage.setItem(
      'tableSettings',
      JSON.stringify({
        tab: MyOrganisationTabs.AssociateSchools,
        tableSettings: associateCenterTableSettings,
        searchQuery,
      })
    );
  }, [associateCenterTableSettings, searchQuery]);

  return (
    <OrgAssociateCentersContext.Provider
      value={{
        associateCenters,
        isLoading,
        error,
        searchQuery,
        associateCenterTableSettings,
        isCenterParent,
        updateSearchQuery,
        updateAssociateCenterTableSettings,
        updateOrgId,
      }}
    >
      {children}
    </OrgAssociateCentersContext.Provider>
  );
};

export default useOrgAssociateCenters;
